/**
 * PATH: packages/email-hub/src/feature/reader-header/workflow/WorkflowRow.tsx
 * FILE_ID: EMAILHUB-READERHEADER-WORKFLOW-ROW-001
 * PURPOSE: The workflow row — Owner / Due / Watchers cells filling the
 *   ExpandedContext Phase 5 slot, each with independent busy state, a routed
 *   context menu, and payload-patch mutations (plan US-22/US-23).
 * ROLE IN ARCHITECTURE: Frame over useWorkflowCells + the reused
 *   email-allocation controls; renders the LaneContextMenu for the three
 *   workflow scopes (ExpandedContext renders it for the four triage lanes).
 * MAIN EXPORTS: WorkflowRow.
 * NON-RESPONSIBILITIES: Mutations (useWorkflowCells), menu vocabulary
 *   (menuScopes/laneMenuItems), overlay geometry (AdaptiveOverlay).
 * NOTES FOR FUTURE AI: Watcher avatar right-clicks capture the target BEFORE
 *   the event bubbles to the lane surface handler — the same routed menu then
 *   shows target-scoped Remove/Ping items. Cells keep a fixed min-height so
 *   loading/optimistic/error states never shift geometry (plan glitch kill).
 *   Empty Owner+Due+Watchers collapse to one "+" (operator 2026-07-26) — same
 *   "facts at rest, mutations on demand" rule as LaneActionsPlus.
 */
import { useMemo, useRef, useState } from 'react'
import { Plus, Users, X } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import {
  AllocationAvatar,
  AssignmentControl,
  DueBucketControl,
  type AllocationMember,
} from '../../components/email-allocation'
import type { Email, EmailWatcher } from '../../hooks/useEmails'
import type { ReaderHeaderSurface } from '../model/types'
import { buildLaneMenuItems, type LaneMenuDeps } from '../menus/laneMenuItems'
import { LaneContextMenu } from '../menus/LaneContextMenu'
import { isWorkflowLane } from '../menus/menuScopes'
import type { ContextMenuRouting } from '../menus/useContextMenuRouting'
import { useWorkflowCells } from './useWorkflowCells'
import { WatcherManager } from './WatcherManager'

const WATCHER_ID_ATTR = 'data-workflow-watcher-id'

export interface WorkflowRowProps {
  email: Email
  members: AllocationMember[]
  /** The ExpandedContext router — ONE menu open across the whole context. */
  routing: ContextMenuRouting
  surface?: ReaderHeaderSurface
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  onPing?: () => void
  onOpenProfile?: (userId: number) => void
}

function Cell({
  label,
  testId,
  children,
  surfaceProps,
  onContextMenuCapture,
}: {
  label: string
  testId: string
  children: React.ReactNode
  surfaceProps: ReturnType<ContextMenuRouting['laneSurfaceProps']>
  onContextMenuCapture?: (event: React.MouseEvent<HTMLElement>) => void
}) {
  return (
    <div
      {...surfaceProps}
      onContextMenuCapture={onContextMenuCapture}
      data-testid={testId}
      className="flex min-h-9 min-w-0 items-center gap-2 px-2 py-1"
    >
      <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  )
}

export function WorkflowRow({
  email,
  members,
  routing,
  surface,
  onPatchEmail,
  onPing,
  onOpenProfile,
}: WorkflowRowProps) {
  const cells = useWorkflowCells({
    email,
    members,
    onPatchEmail,
    surface,
    onPing,
    onOpenProfile,
  })
  const { owner, due, watchers } = cells
  const watchersTriggerRef = useRef<HTMLButtonElement>(null)
  // Watcher avatar the operator right-clicked; scopes the routed menu's
  // Remove/Ping items to that person. Cleared when the menu closes.
  const [targetWatcher, setTargetWatcher] = useState<EmailWatcher | null>(null)
  // When nothing is assigned yet, the three empty cells are noise — collapse
  // behind one "+" until the operator asks for them.
  const [workflowOpen, setWorkflowOpen] = useState(false)

  const ownerEmpty = owner.assigneeUserId == null
  const dueEmpty =
    due.currentBucket === 'no_date' && due.scheduledForLabel == null
  const watchersEmpty = watchers.watcherList.length === 0
  const allEmpty = ownerEmpty && dueEmpty && watchersEmpty
  const showCollapsed = allEmpty && !workflowOpen

  const openMenu = routing.openMenu
  const workflowMenu =
    openMenu && isWorkflowLane(openMenu.scope.lane) ? openMenu : null

  const menuDeps = useMemo<LaneMenuDeps>(() => {
    const assigneeId = owner.assignment?.assignee_user_id ?? null
    const hasDue =
      due.currentBucket !== 'no_date' || due.scheduledForLabel != null
    return {
      workflow: {
        changeOwner: () => owner.setShowPicker(true),
        clearOwner:
          assigneeId != null ? () => void owner.pickAssignee(null) : undefined,
        pingOwner: assigneeId != null && onPing ? onPing : undefined,
        openOwnerProfile:
          assigneeId != null && onOpenProfile
            ? () => onOpenProfile(assigneeId)
            : undefined,
        dueToday: () => void due.pickBucket('today'),
        dueTomorrow: () => void due.pickBucket('tomorrow'),
        dueThisWeek: () => void due.pickBucket('this_week'),
        duePickDate: () => due.setPickerOpen(true),
        dueClear: hasDue ? () => void due.clearDue() : undefined,
        copyDate: due.scheduledForLabel != null ? due.copyDate : undefined,
        inviteWatcher: () => watchers.setShowPicker(true),
        removeWatcher: targetWatcher
          ? () => void watchers.removeWatcher(targetWatcher)
          : undefined,
        removeWatcherLabel: targetWatcher
          ? `Remove ${targetWatcher.user_name || targetWatcher.user_email}`
          : undefined,
        pingWatcher: targetWatcher && onPing ? onPing : undefined,
        copyWatcherList:
          watchers.watcherList.length > 0 ? watchers.copyList : undefined,
      },
    }
  }, [owner, due, watchers, targetWatcher, onPing, onOpenProfile])

  const captureWatcherTarget = (event: React.MouseEvent<HTMLElement>) => {
    const el =
      event.target instanceof Element
        ? event.target.closest(`[${WATCHER_ID_ATTR}]`)
        : null
    const id = el ? Number(el.getAttribute(WATCHER_ID_ATTR)) : NaN
    setTargetWatcher(
      watchers.watcherList.find((w) => w.user_id === id) ?? null,
    )
  }

  if (showCollapsed) {
    return (
      <div
        data-testid="reader-workflow-row"
        data-workflow-collapsed="true"
        className="flex items-center gap-1.5 px-0.5 py-0.5"
      >
        <button
          type="button"
          onClick={() => setWorkflowOpen(true)}
          data-testid="workflow-actions-plus"
          aria-label="Assign owner, due date, or watchers"
          title="Assign owner, due date, or watchers"
          className="inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-[12px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        >
          <Plus className="h-3 w-3" aria-hidden />
          Add assignment
        </button>
      </div>
    )
  }

  return (
    <div
      data-testid="reader-workflow-row"
      data-workflow-collapsed="false"
      className="grid grid-cols-1 divide-y divide-border/40 sm:grid-cols-3 sm:divide-x sm:divide-y-0"
    >
      {allEmpty && (
        <div className="col-span-full flex justify-end px-2 pt-1 sm:col-span-3">
          <button
            type="button"
            onClick={() => setWorkflowOpen(false)}
            data-testid="workflow-actions-collapse"
            aria-label="Hide workflow actions"
            className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </div>
      )}
      <Cell
        label="Owner"
        testId="workflow-cell-owner"
        surfaceProps={routing.laneSurfaceProps('workflowOwner')}
      >
        <AssignmentControl vm={owner} showLabel={false} />
        {owner.status === 'departed' && (
          <span
            className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-px text-[9px] font-medium text-amber-700 dark:text-amber-300"
            data-testid="workflow-owner-departed"
          >
            departed
          </span>
        )}
      </Cell>
      <Cell
        label="Due"
        testId="workflow-cell-due"
        surfaceProps={routing.laneSurfaceProps('workflowDue')}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <DueBucketControl
            vm={due}
            compact
            onPickDate={(iso) => void due.pickDate(iso)}
            open={due.pickerOpen}
            onOpenChange={due.setPickerOpen}
          />
          {due.scheduledForLabel && (
            <span
              className="truncate text-[11px] text-muted-foreground"
              data-testid="workflow-due-date"
            >
              {due.scheduledForLabel}
            </span>
          )}
        </div>
      </Cell>
      <Cell
        label="Watchers"
        testId="workflow-cell-watchers"
        surfaceProps={routing.laneSurfaceProps('workflowWatchers')}
        onContextMenuCapture={captureWatcherTarget}
      >
        {/* WORK ITEM 0: 2 avatars + overflow badge + Manage button is the
            widest content this cell ever renders (6-watcher golden fixture
            overflowed the 214px grid track by 3px at 660px). gap-0.5 and
            tighter chip padding below compact it back under budget without
            touching Owner/Due, which never approach this width. */}
        <div className="flex min-w-0 items-center gap-0.5">
          {watchers.visible.map((watcher) => (
            <span
              key={watcher.user_id}
              {...{ [WATCHER_ID_ATTR]: String(watcher.user_id) }}
              title={watcher.user_name || watcher.user_email}
              data-testid={`workflow-watcher-${watcher.user_id}`}
            >
              <AllocationAvatar
                size="xs"
                name={watcher.user_name}
                email={watcher.user_email}
                avatar_url={
                  members.find((m) => m.id === watcher.user_id)?.avatar_url
                }
              />
            </span>
          ))}
          {watchers.overflowCount > 0 && (
            <span
              className="rounded-full bg-muted px-1 py-px text-[10px] tabular-nums text-muted-foreground"
              data-testid="workflow-watchers-overflow"
            >
              +{watchers.overflowCount}
            </span>
          )}
          <button
            ref={watchersTriggerRef}
            type="button"
            onClick={() => watchers.setShowPicker(!watchers.showPicker)}
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full border border-dashed border-border px-1.5 py-0.5',
              'text-[11px] text-muted-foreground transition hover:bg-muted/60 pointer-coarse:min-h-11',
              watchers.busyKey && 'opacity-50',
            )}
            data-testid="workflow-watchers-manage"
            aria-haspopup="dialog"
            aria-expanded={watchers.showPicker}
          >
            <Users className="h-3 w-3" />
            <span>{watchers.watcherList.length === 0 ? 'Invite' : 'Manage'}</span>
          </button>
        </div>
        <WatcherManager vm={watchers} anchorRef={watchersTriggerRef} />
      </Cell>
      {workflowMenu && (
        <LaneContextMenu
          menu={workflowMenu}
          items={buildLaneMenuItems(workflowMenu.scope, menuDeps)}
          onClose={() => {
            setTargetWatcher(null)
            routing.closeMenu()
          }}
        />
      )}
    </div>
  )
}
